import { useMutation } from '@tanstack/react-query';
import { isNil } from 'lodash';
import { useSnackbar } from 'notistack';
import type { IntelligenceExcelExport } from 'api/models/IntelligenceExcelExport';
import { FirmsService } from 'api/services/FirmsService';
import { useStore } from 'common/store';
import type { SummaryDefinition } from 'common/types/summaryDefinitions';
import { useFirmMeasurementDateFilter } from 'pages/Firms/hooks/useFirmMeasurementDateFilter';
import { useIntelligenceContext } from 'pages/Intelligence/IntelligenceContext';
import { FirmService } from 'services';
import { getObjectValue, isValidDate } from 'utilities';
import { buildSpreadsheetExportPayload } from './buildIntelligenceSummaryWorkbook';

const firmService = new FirmService();

type SummaryType = 'firm' | 'valuations';

type ExportIntelligenceSummaryParams = {
  definition: SummaryDefinition;
  summaryType?: SummaryType;
  filename?: string;
  // The tab label the user sees; the default tab reads "Firm Summary" whatever its stored name.
  sheetName?: string;
};

const fetchSpreadsheetData = async (
  firmId: number,
  definitionId: number,
  summaryType: SummaryType,
  measurementDate?: string,
  fundId?: number
) =>
  summaryType === 'valuations'
    ? FirmsService.apiFirmsValuationsDefinitionsDataRead(
      String(firmId),
      String(definitionId),
      measurementDate || undefined,
      fundId
    )
    : FirmsService.apiFirmsSummaryDefinitionsDataRead(
      String(firmId),
      String(definitionId),
      measurementDate || undefined,
      fundId
    );

const useExportIntelligenceSummary = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [storeValue] = useStore();
  const currentFirm = getObjectValue(getObjectValue(storeValue).firmInfo);
  const firmId = Number(currentFirm.id) || undefined;
  const firmSlug = String(currentFirm.slug || 'firm');

  const { isMostRecentMeasurementDate, selectedMeasurementDate } = useFirmMeasurementDateFilter();
  const { format, isAllFundsSelected, selectedFundId } = useIntelligenceContext();

  const resolvedMeasurementDate =
    !isNil(selectedMeasurementDate) && isValidDate(selectedMeasurementDate.date)
      ? selectedMeasurementDate.date
      : undefined;
  const measurementDate = isMostRecentMeasurementDate ? undefined : resolvedMeasurementDate;
  const fundId = isAllFundsSelected ? undefined : (selectedFundId ?? undefined);
  const currencyCode = String(format?.currency?.code || 'USD');
  const units = String(format?.currency?.units || '');
  const subtitle = [
    measurementDate ? `As of ${measurementDate}` : 'Most Recent',
    units ? `${currencyCode} in ${units.toLowerCase()}` : currencyCode,
  ].join(' · ');

  const download = async (
    definition: SummaryDefinition,
    summaryType: SummaryType,
    payload: IntelligenceExcelExport,
    filename: string
  ) => {
    const blob = await firmService.exportIntelligenceSummary(
      firmId as number,
      definition.id,
      summaryType,
      payload
    );
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  };

  return useMutation({
    mutationFn: async ({ definition, summaryType = 'firm', filename, sheetName }: ExportIntelligenceSummaryParams) => {
      if (!firmId) {
        throw new Error('Missing firm');
      }

      const resolvedSheetName = sheetName || definition.name;
      const resolvedFilename =
        filename || buildIntelligenceExportFilename(firmSlug, resolvedSheetName, measurementDate);

      const data = await fetchSpreadsheetData(firmId, definition.id, summaryType, measurementDate, fundId);
      await download(
        definition,
        summaryType,
        { ...buildSpreadsheetExportPayload(definition, data, format, subtitle), sheet_name: resolvedSheetName },
        resolvedFilename
      );
    },
    onError: () => {
      enqueueSnackbar('Unable to export this Intelligence summary. Please try again later.', {
        variant: 'error',
      });
    },
  });
};

export const buildIntelligenceExportFilename = (firmSlug: string, definitionName: string, measurementDate?: string) => {
  const safeName = definitionName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  const datePart = measurementDate || 'most-recent';
  return `${firmSlug}-${safeName || 'summary'}-${datePart}.xlsx`;
};

export default useExportIntelligenceSummary;
